import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { of, BehaviorSubject, from, combineLatest, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import * as firebase from 'firebase/app';
import { UserService } from '../user/user.service';
import { Router } from '@angular/router';
import { UIControlService } from '../shared/uicontrol.service';

@Injectable({
  providedIn: 'root'
})
export class IdentityService
{
  private signUpFailedMessage =
    'Registation Failed. Please try again in a few minutes.';
  private logInFailedMessage =
    'Registation Failed. Please try again in a few minutes.';

  constructor (
    public fireAuth: AngularFireAuth,
    private ui: UIControlService,
    private userService: UserService,
    private router: Router
  )
  {
    this.fireAuth.authState.subscribe(result =>
      this.userLoggedInChanged.next(!!result)
    );
  }

  public userDisplayName = this.fireAuth.user.pipe(map(u => u?.displayName));

  private userLoggedInChanged = new BehaviorSubject<boolean>(false);

  public userLoggedIn$ = this.userLoggedInChanged.asObservable();

  public userKey$ = this.fireAuth.user.pipe(map(u => u?.uid));

  public userProfileComplete$ = this.fireAuth.user.pipe(
    switchMap(u =>
    {
      if (!!u)
      {
        return this.userService.getUserDetails(u).snapshotChanges();
      } else
      {
        return of(null);
      }
    }),
    map(ud =>
    {
      if (!!ud)
      {
        return ud.payload.data()?.profileComplete ?? false;
      } else
      {
        return true;
      }
    })
  );

  public userIsServiceProvider$ = this.fireAuth.user.pipe(
    switchMap(u =>
    {
      if (!!u)
      {
        return this.userService.getUserDetails(u).snapshotChanges();
      } else
      {
        return of(null);
      }
    }),
    map(ud =>
    {
      if (!!ud)
      {
        return ud.payload.data()?.isServiceProvider ?? false;
      } else
      {
        return true;
      }
    })
  );

  public signOut()
  {
    this.fireAuth.auth.signOut();
    this.router.navigateByUrl('');
  }

  public handleAuthenticationReroute(user?: any)
  {
    if (!(user == null || user == undefined))
      this.userService.createUserDetailsRecord(user);

    this.userProfileComplete$.subscribe(isProfileComplete =>
    {
      if (isProfileComplete)
      {
        this.router.navigateByUrl('');
      } else
      {
        this.router.navigateByUrl('my-profile');
      }
    });
  }

  public async signInWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<any>
  {
    try {
      return this.fireAuth.auth.signInWithEmailAndPassword(email, password);
    } catch (error)
    {
      this.ui.notifyFailure(this.logInFailedMessage);
    }
  }

  signUpWithEmailAndPassword(
    displayName: string,
    email: string,
    password: string
  ): Observable<any>
  {
    try {
      return from(
        this.fireAuth.auth.createUserWithEmailAndPassword(email, password)
      ).pipe(
        switchMap(credentials =>
          this.handleUserSignup(displayName, credentials)
        ),
        tap(r =>
        {
          console.log(r);
        })
      );
    } catch (error)
    {
      this.ui.notifyFailure(this.signUpFailedMessage);
    }
  }

  private handleUserSignup(
    displayName: string,
    credentials: firebase.auth.UserCredential
  ): Observable<any>
  {
    return from(
      this.fireAuth.auth.currentUser.updateProfile({ displayName })
    ).pipe(
      switchMap(() =>
        this.userService.createUserDetailsRecord(credentials.user)
      )
    );
  }

  public async signInWithPhoneNumber(phoneNumber: string)
  {
    try
    {
      this.fireAuth.auth.useDeviceLanguage();
      const recaptcha = new firebase.auth.RecaptchaVerifier('sign-in-button', {
        size: 'invisible',
        'expired-callback'(response)
        {
          this.phoneSignUpFailedCallback(response);
        }
      });

      const result = await this.fireAuth.auth
        .signInWithPhoneNumber(phoneNumber, recaptcha)
        .catch(error =>
        {
          console.error(error);
        });
      return result;
    } catch (error)
    {
      this.ui.notifyFailure(this.signUpFailedMessage);
    }
  }

  public async confirmPhoneNumberSignUp(
    displayName: string,
    confirmationResult: firebase.auth.ConfirmationResult,
    confirmationCode: string
  ): Promise<boolean>
  {
    return confirmationResult
      .confirm(confirmationCode)
      .then(async authResult =>
      {
        await authResult.user.updateProfile({ displayName });
        this.fireAuth.auth.updateCurrentUser(authResult.user);
        this.userService.createUserDetailsRecord(
          this.fireAuth.auth.currentUser
        );
        return true;
      })
      .catch(error =>
      {
        console.error(error);
        return false;
      });
  }

  public async confirmPhoneNumber(
    confirmationResult: firebase.auth.ConfirmationResult,
    confirmationCode: string
  ): Promise<boolean>
  {
    return confirmationResult
      .confirm(confirmationCode)
      .then(async authResult =>
      {
        this.fireAuth.auth.updateCurrentUser(authResult.user);
        return true;
      })
      .catch(async error =>
      {
        return false;
      });
  }

  public async phoneSignUpFailedCallback(response)
  {
    console.error(response);
  }
}
